import React, { useCallback, useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import Sheet from "@mui/joy/Sheet";
import {
    Box,
    Checkbox,
    Divider,
    Link,
    List,
    ListItem,
    ListItemButton,
    Stack,
    Theme,
    Typography,
} from "@mui/joy";
import MenuItem from "@mui/joy/MenuItem";

import {
    useStytchB2BClient,
    useStytchMember,
    useStytchMemberSession,
    useStytchOrganization,
} from "@stytch/react/b2b";
import { useIsAdmin } from "../hooks/useIsAdmin";

import {
    ChatRounded,
    FolderRounded,
    AccountTreeRounded,
    FactCheckOutlined,
} from "@mui/icons-material";
import Dropdown from "@mui/joy/Dropdown";
import MenuButton from "@mui/joy/MenuButton";
import Menu from "@mui/joy/Menu";

import {
    hasNoConversations,
    setHasNoConversations,
    setShowDebugInfo,
    setShowEmbeddingText,
    useHasRichDocuments,
    useHasWorkflows,
    useShowDebugInfo,
    useShowEmbeddingText,
} from "../hooks/configuration";

import LogoDark from "./LogoDark";
import DocumentList from "./DocumentList";
import HoverDropdown from "./HoverDropdown";
import ConversationList from "./ConversationList";
import { clearFileCache } from "./fileviewer/loadFileAndCache";
import { useMutation } from "react-query";
import { WorkflowApi } from "../api/workflow";

const styles = {
    headerWrapper: (theme: Theme) => ({
        flex: "0 0 auto",
        py: 1,
        px: 3,
        zIndex: 999,
        boxShadow: theme.vars.shadow.md,
    }),
    headerStack: { flexGrow: 1 },
    whiteText: { color: "white !important" },

    menu: { zIndex: 9999 },
    adminTogglesContainer: { py: 1, px: 2, mt: 1 },
};

export default function Header() {
    const { member } = useStytchMember();
    const { organization } = useStytchOrganization();
    const { session } = useStytchMemberSession();
    const client = useStytchB2BClient();
    const isAdmin = useIsAdmin();

    const hasRichDocuments = useHasRichDocuments();
    const hasWorkflows = useHasWorkflows();

    const [showDebugInfoToggle, setShowDebugInfoToggle] = useState(useShowDebugInfo());
    const handleDebugInfoToggleChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setShowDebugInfoToggle(newValue);
        setShowDebugInfo(newValue);
    };

    const [showEmbeddingTextToggle, setShowEmbeddingTextToggle] = useState(useShowEmbeddingText());
    const handleEmbeddingTextToggleChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setShowEmbeddingTextToggle(newValue);
        setShowEmbeddingText(newValue);
    };

    const [hasEmptyHistoryToggle, setHasEmptyHistoryToggle] = useState(hasNoConversations());
    const handleEmptyHistoryToggleChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setHasEmptyHistoryToggle(newValue);
        setHasNoConversations(newValue);
    };

    const logoutCallback = useCallback(() => {
        clearFileCache();
        client.session.revoke();
    }, [client.session]);

    const requestConstructabilityReviewMutation = useMutation(
        () => WorkflowApi.startFindProblems(),
        {
            onSuccess: () => {
                alert("Run requested ...");
            },
            onError: () => {
                alert("Failed to start run");
            },
        },
    );
    const handleRequestConstructabilityReview = useCallback(() => {
        requestConstructabilityReviewMutation.mutate();
    }, [requestConstructabilityReviewMutation]);

    return (
        <Sheet sx={styles.headerWrapper} color="primary" variant="solid" invertedColors>
            <Stack
                direction="row"
                sx={styles.headerStack}
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" spacing={2}>
                    <Link
                        to="/"
                        variant="plain"
                        color="neutral"
                        component={ReactRouterLink}
                        underline="none"
                    >
                        <LogoDark />
                    </Link>
                </Stack>

                {session && hasRichDocuments && (
                    <Stack direction="row" spacing={2}>
                        <HoverDropdown
                            buttonLabel="Documents"
                            startDecorator={<FolderRounded />}
                            buttonProps={{ sx: styles.whiteText, variant: "plain" }}
                        >
                            <DocumentList />
                        </HoverDropdown>

                        <HoverDropdown
                            buttonLabel="Questions"
                            startDecorator={<ChatRounded />}
                            buttonProps={{ sx: styles.whiteText, variant: "plain" }}
                        >
                            <Box sx={{ maxHeight: "80vh" }}>
                                <ConversationList isHeaderDropdown={true} />
                            </Box>
                        </HoverDropdown>

                        {hasWorkflows && (
                            <HoverDropdown
                                buttonLabel="Tools"
                                startDecorator={<AccountTreeRounded />}
                                buttonProps={{ sx: styles.whiteText, variant: "plain" }}
                            >
                                <List>
                                    <ListItem>
                                        <ListItemButton component={ReactRouterLink} to={"/w/cr"}>
                                            <Typography
                                                noWrap
                                                fontSize="sm"
                                                sx={{ flex: 1 }}
                                                startDecorator={<FactCheckOutlined />}
                                            >
                                                Constructability Review
                                            </Typography>
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                            </HoverDropdown>
                        )}
                    </Stack>
                )}

                <Stack>
                    {session && (
                        <Dropdown>
                            <MenuButton variant="plain" sx={styles.whiteText}>
                                {`${member?.name} (${organization?.organization_name})`}
                            </MenuButton>

                            <Menu sx={styles.menu}>
                                {isAdmin && (
                                    <>
                                        <ListItem sticky>
                                            <Typography level="title-sm">Admin stuff</Typography>
                                        </ListItem>
                                        <MenuItem>
                                            <Link href="/admin" color="neutral" underline="none">
                                                Overview
                                            </Link>
                                        </MenuItem>
                                        <MenuItem>
                                            <Link href="/admin/kg" color="neutral" underline="none">
                                                KG remap
                                            </Link>
                                        </MenuItem>
                                        <MenuItem>
                                            <Link
                                                href="/admin/fileCache"
                                                color="neutral"
                                                underline="none"
                                            >
                                                Browser file cache
                                            </Link>
                                        </MenuItem>
                                        <MenuItem>
                                            <Link
                                                href="#"
                                                color="neutral"
                                                underline="none"
                                                onClick={handleRequestConstructabilityReview}
                                            >
                                                Request constructability review
                                            </Link>
                                        </MenuItem>
                                    </>
                                )}
                                {isAdmin && (
                                    <>
                                        <Divider />
                                        <Stack sx={styles.adminTogglesContainer} spacing={1}>
                                            <Typography level="title-sm">Toggles</Typography>
                                            <Checkbox
                                                label="Show debug info"
                                                variant="soft"
                                                checked={showDebugInfoToggle}
                                                onChange={handleDebugInfoToggleChanged}
                                            />
                                            <Checkbox
                                                label="Show embedding text"
                                                variant="soft"
                                                checked={showEmbeddingTextToggle}
                                                onChange={handleEmbeddingTextToggleChanged}
                                            />
                                            <Checkbox
                                                label="No conversations visible"
                                                variant="soft"
                                                checked={hasEmptyHistoryToggle}
                                                onChange={handleEmptyHistoryToggleChanged}
                                            />
                                        </Stack>
                                    </>
                                )}

                                <MenuItem onClick={logoutCallback}>
                                    <Link href="#" color="neutral" underline="none">
                                        Logout
                                    </Link>
                                </MenuItem>
                            </Menu>
                        </Dropdown>
                    )}
                </Stack>
            </Stack>
        </Sheet>
    );
}
